/**
 * The door every screen inside the logged-in layout goes through.
 *
 * Menu visibility is not access control: a page kept out of the drawer is
 * still one typed URL away, and /support carries every tenant's conversations.
 * The level that opens a screen lives in `navbar/route-access.ts`, which the
 * drawer reads too, so the menu and the URL cannot disagree. The API scopes
 * what it returns regardless — this is about the page itself being ours.
 *
 * A reporter sent to /support is almost always following the link in their own
 * notification mail, so bouncing them to the dashboard and saying nothing
 * makes their own conversation look broken. The ticket they asked for opens
 * in the bubble's panel instead, and only then do they go home.
 *
 * Home is /todo, not "/". "/" is the landing page, which sits OUTSIDE the App
 * layout and redirects here anyway — and that hop unmounts App, taking the
 * panel's provider and the conversation just opened in it down with it.
 */
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useGlobalState } from "./globalState";
import { accessLevelForPath, canOpen } from "./navbar/route-access";
import { PageLoader } from "./page-loader";
import { useSupportReport } from "./support/use-support-report";

/** The ticket id in `/support/17`, or null anywhere else under /support. */
const requestedTicketId = (pathname: string): number | null => {
	const match = /^\/support\/(\d+)/.exec(pathname);
	return match ? Number(match[1]) : null;
};

const AccessRoute: React.FC = () => {
	const { currentUser } = useGlobalState();
	const { pathname } = useLocation();
	const { openConversation } = useSupportReport();
	// The path the panel was already told about, so a second denied visit to
	// another ticket is handled too.
	const [handledPath, setHandledPath] = useState<string | null>(null);

	const level = accessLevelForPath(pathname);
	const isDenied =
		Boolean(currentUser) && level !== undefined && !canOpen(level, currentUser);
	const wanted = requestedTicketId(pathname);

	// Rendering <Navigate> straight away would unmount this before the effect
	// ever ran — a child's effects fire first, and Navigate's is the
	// navigation. So the redirect waits one commit for the panel to be told.
	useEffect(() => {
		if (!isDenied || handledPath === pathname) return;
		if (wanted !== null) openConversation(wanted);
		setHandledPath(pathname);
	}, [isDenied, handledPath, pathname, wanted, openConversation]);

	if (level === undefined || level === "everyone") return <Outlet />;
	// Nobody is anybody until the app has fetched who they are; redirecting on
	// the way there would throw a staff member off their own page.
	if (!currentUser) return <PageLoader />;
	if (!isDenied) return <Outlet />;
	if (handledPath !== pathname) return <PageLoader />;
	return <Navigate to="/todo" replace />;
};

export default AccessRoute;
